import {FeatureReferenceBinding, type FeatureBindingResolveContext, type FeatureBindingResolvedValue, type SerializedSketchProfileFeatureBinding} from './FeatureReferenceBinding';
import {resolveSketchProfileBindingFromViewer} from '../../utils/feature';

/**
 * 草图轮廓选择项。
 */
export interface SketchProfileBindingSelectionItem {
    /** 来源草图 ID。 */
    sketchId: string;
    /** 轮廓来源对象 ID。 */
    contourSourceObjectId?: string;
    /** 轮廓键。 */
    contourKey?: string;
    /** 回退区域几何。 */
    regionGeometry: ProfileRegionGeometry;
    /** 相交闭合区域身份。 */
    regionIdentity?: PersistedRegionIdentity;
}

/**
 * 草图轮廓绑定对象。
 */
export class SketchProfileFeatureBinding extends FeatureReferenceBinding {
    /** 绑定类型。 */
    public readonly kind = 'sketch-profile' as const;
    /** 序列化载荷。 */
    private readonly payload: SerializedSketchProfileFeatureBinding;

    /**
     * 构造函数。
     * @param payload 绑定载荷。
     */
    public constructor(payload: SerializedSketchProfileFeatureBinding) {
        super();
        this.payload = {
            ...payload,
            contourSourceObjectIds: payload.contourSourceObjectIds ? [...payload.contourSourceObjectIds] : undefined,
            contourKeys: payload.contourKeys ? [...payload.contourKeys] : undefined,
            regionIdentities: payload.regionIdentities ? payload.regionIdentities.map((identity) => structuredClone(identity)) : undefined,
            fallbackRegionGeometries: payload.fallbackRegionGeometries
                ? payload.fallbackRegionGeometries.map((regionGeometry) => structuredClone(regionGeometry))
                : undefined,
            runtimeSketchPlane: payload.runtimeSketchPlane ? structuredClone(payload.runtimeSketchPlane) : undefined,
            runtimeSketchEntities: payload.runtimeSketchEntities
                ? payload.runtimeSketchEntities.map((entity) => structuredClone(entity))
                : undefined,
        };
    }

    /**
     * 从轮廓选择结果创建绑定载荷。
     * @param input 输入参数。
     * @returns 绑定载荷。
     */
    public static createPayload(input: {
        sketchId: string;
        contours: SketchProfileBindingSelectionItem[];
        runtimeSketchPlane?: RuntimeSketchPlaneInput;
        runtimeSketchEntities?: RuntimeSketchEntityInput[];
    }): SerializedSketchProfileFeatureBinding {
        return {
            kind: 'sketch-profile',
            sketchId: input.sketchId,
            contourSourceObjectIds: input.contours
                .map((contour) => contour.contourSourceObjectId)
                .filter((value): value is string => typeof value === 'string' && value.length > 0),
            contourKeys: input.contours
                .map((contour) => contour.contourKey)
                .filter((value): value is string => typeof value === 'string' && value.length > 0),
            regionIdentities: input.contours
                .map((contour) => contour.regionIdentity)
                .filter((value): value is PersistedRegionIdentity => value !== undefined)
                .map((identity) => structuredClone(identity)),
            fallbackRegionGeometries: input.contours.map((contour) => structuredClone(contour.regionGeometry)),
            runtimeSketchPlane: input.runtimeSketchPlane ? structuredClone(input.runtimeSketchPlane) : undefined,
            runtimeSketchEntities: input.runtimeSketchEntities
                ? input.runtimeSketchEntities.map((entity) => structuredClone(entity))
                : undefined,
        };
    }

    /**
     * @inheritdoc
     */
    public serialize(): SerializedSketchProfileFeatureBinding {
        return structuredClone(this.payload);
    }

    /**
     * @inheritdoc
     */
    public clone(): SketchProfileFeatureBinding {
        return new SketchProfileFeatureBinding(this.serialize());
    }

    /**
     * @inheritdoc
     */
    public dependsOnSketch(sketchId: string): boolean {
        return this.payload.sketchId === sketchId;
    }

    /**
     * @inheritdoc
     */
    public resolve(context: FeatureBindingResolveContext): FeatureBindingResolvedValue | null {
        return resolveSketchProfileBindingFromViewer(context.viewer, this.payload);
    }
}
